import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/hooks/useAuth";
import ErrorBoundary from "@/components/shared/ErrorBoundary";
import ScrollToTop from "@/components/ScrollToTop";
import LoadingSpinner from "@/components/shared/LoadingSpinner";

// Eagerly loaded - homepage
import Index from "./pages/Index";

// Lazy loaded routes
const NotFound = lazy(() => import("./pages/NotFound"));
const ClientAuth = lazy(() => import("./pages/ClientAuth"));
const AdminAuth = lazy(() => import("./pages/AdminAuth"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const ChangePassword = lazy(() => import("./pages/ChangePassword"));
const PaymentRequired = lazy(() => import("./pages/PaymentRequired"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const DataProcessingAddendum = lazy(() => import("./pages/DataProcessingAddendum"));
const Subprocessors = lazy(() => import("./pages/Subprocessors"));
const About = lazy(() => import("./pages/About"));
const Products = lazy(() => import("./pages/Products"));
const MobileApp = lazy(() => import("./pages/MobileApp"));
const BrandPreview = lazy(() => import("./pages/BrandPreview"));
const WebsiteReview = lazy(() => import("./pages/WebsiteReview"));
const ProtectedRoute = lazy(() => import("./components/auth/ProtectedRoute"));
const AdminProtectedRoute = lazy(() => import("./components/auth/AdminProtectedRoute"));
const ClientDashboard = lazy(() => import("./pages/client/ClientDashboard"));
const ClientBrand = lazy(() => import("./pages/client/ClientBrand"));
const ClientRequests = lazy(() => import("./pages/client/ClientRequests"));
const ClientRequestDetail = lazy(() => import("./pages/client/ClientRequestDetail"));
const ClientBilling = lazy(() => import("./pages/client/ClientBilling"));
const AdminRequestsQueue = lazy(() => import("./pages/admin/AdminRequestsQueue"));
const AdminRequestDetail = lazy(() => import("./pages/admin/AdminRequestDetail"));
const AdminFinance = lazy(() => import("./pages/admin/AdminFinance"));
const AdminExpenses = lazy(() => import("./pages/admin/AdminExpenses"));
const AdminExpenseForm = lazy(() => import("./pages/admin/AdminExpenseForm"));
const AdminExpensesByClient = lazy(() => import("./pages/admin/AdminExpensesByClient"));
const AdminClientExpenses = lazy(() => import("./pages/admin/AdminClientExpenses"));
const AdminManualIncome = lazy(() => import("./pages/admin/AdminManualIncome"));
const AdminManualIncomeForm = lazy(() => import("./pages/admin/AdminManualIncomeForm"));
const AdminStripeIncome = lazy(() => import("./pages/admin/AdminStripeIncome"));
const AdminAllRevenue = lazy(() => import("./pages/admin/AdminAllRevenue"));
const AdminProfitReport = lazy(() => import("./pages/admin/AdminProfitReport"));
const AdminAuditLog = lazy(() => import("./pages/admin/AdminAuditLog"));
const AdminAccounts = lazy(() => import("./pages/admin/AdminAccounts"));
const AdminAccountDetail = lazy(() => import("./pages/admin/AdminAccountDetail"));
const AdminForecastScenarios = lazy(() => import("./pages/admin/AdminForecastScenarios"));
const AdminForecastDetail = lazy(() => import("./pages/admin/AdminForecastDetail"));
const ClientContracts = lazy(() => import("./pages/client/ClientContracts"));
const AdminVault = lazy(() => import("./pages/admin/AdminVault"));
const AdminClientAssets = lazy(() => import("./pages/admin/AdminClientAssets"));
const AdminTeam = lazy(() => import("./pages/admin/AdminTeam"));
const AdminLogoOrders = lazy(() => import("./pages/admin/AdminLogoOrders"));
const AdminLeads = lazy(() => import("./pages/admin/AdminLeads"));
const AdminTrustedPartners = lazy(() => import("./pages/admin/AdminTrustedPartners"));
const AdminLeadDetail = lazy(() => import("./pages/admin/AdminLeadDetail"));
const AdminWebsiteReviews = lazy(() => import("./pages/admin/AdminWebsiteReviews"));
const AdminHomepagePreviews = lazy(() => import("./pages/admin/AdminHomepagePreviews"));
const TheRealtyCapecodPreview = lazy(() => import("./pages/TheRealtyCapecodPreview"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ChazPreview = lazy(() => import("./pages/ChazPreview"));
const ChazPreview1 = lazy(() => import("./pages/ChazPreview1"));
const ChazPreview2 = lazy(() => import("./pages/ChazPreview2"));
const ChazPreview3 = lazy(() => import("./pages/ChazPreview3"));
const ChazPreview4 = lazy(() => import("./pages/ChazPreview4"));
const ChazPreview5 = lazy(() => import("./pages/ChazPreview5"));
const MollyIndex = lazy(() => import("./pages/mollyblack/MollyIndex"));
const MollyAbout = lazy(() => import("./pages/mollyblack/MollyAbout"));
const MollyListings = lazy(() => import("./pages/mollyblack/MollyListings"));
const MollyListingDetail = lazy(() => import("./pages/mollyblack/MollyListingDetail"));
const MollyContact = lazy(() => import("./pages/mollyblack/MollyContact"));
const MollyPastTransactions = lazy(() => import("./pages/mollyblack/MollyPastTransactions"));

const queryClient = new QueryClient();

// Suspense fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <LoadingSpinner size="lg" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <ErrorBoundary>
        <AuthProvider>
          <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<Index />} />
                <Route path="/auth" element={<ClientAuth />} />
                <Route path="/client-portal/auth" element={<ClientAuth />} />
                <Route path="/admin/login" element={<AdminAuth />} />
                <Route path="/admin/auth" element={<AdminAuth />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/dpa" element={<DataProcessingAddendum />} />
                <Route path="/subprocessors" element={<Subprocessors />} />
                <Route path="/about" element={<About />} />
                <Route path="/products" element={<Products />} />
                <Route path="/products/mobile-app" element={<MobileApp />} />
                <Route path="/brand-preview" element={<BrandPreview />} />
                <Route path="/website-review" element={<WebsiteReview />} />
                <Route path="/therealtycapecod-preview" element={<TheRealtyCapecodPreview />} />
                <Route path="/chaz" element={<ChazPreview />} />
                <Route path="/chaz1" element={<ChazPreview1 />} />
                <Route path="/chaz2" element={<ChazPreview2 />} />
                <Route path="/chaz3" element={<ChazPreview3 />} />
                <Route path="/chaz4" element={<ChazPreview4 />} />
                <Route path="/chaz5" element={<ChazPreview5 />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* Molly Black mini-site */}
                <Route path="/mollyblack" element={<MollyIndex />} />
                <Route path="/mollyblack/about" element={<MollyAbout />} />
                <Route path="/mollyblack/listings" element={<MollyListings />} />
                <Route path="/mollyblack/listings/:slug" element={<MollyListingDetail />} />
                <Route path="/mollyblack/contact" element={<MollyContact />} />
                <Route path="/mollyblack/past-transactions" element={<MollyPastTransactions />} />
                
                {/* Protected client portal routes */}
                <Route 
                  path="/portal" 
                  element={
                    <ProtectedRoute>
                      <ClientDashboard />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/portal/brand" 
                  element={
                    <ProtectedRoute>
                      <ClientBrand />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/portal/requests" 
                  element={
                    <ProtectedRoute>
                      <ClientRequests />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/portal/requests/:id" 
                  element={
                    <ProtectedRoute>
                      <ClientRequestDetail />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/portal/billing" 
                  element={
                    <ProtectedRoute>
                      <ClientBilling />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/portal/contracts" 
                  element={
                    <ProtectedRoute>
                      <ClientContracts />
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="/client-portal" 
                  element={
                    <ProtectedRoute>
                      <ClientDashboard />
                    </ProtectedRoute>
                  } 
                />
                
                <Route path="/change-password" element={<ChangePassword />} />
                <Route path="/payment-required" element={<PaymentRequired />} />
                
{/* Protected admin routes */}
                <Route 
                  path="/admin" 
                  element={
                    <AdminProtectedRoute>
                      <AdminDashboard />
                    </AdminProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/requests" 
                  element={
                    <AdminProtectedRoute>
                      <AdminRequestsQueue />
                    </AdminProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/accounts" 
                  element={
                    <AdminProtectedRoute requiredPermission="canAccessAccounts">
                      <AdminAccounts />
                    </AdminProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/accounts/:id" 
                  element={
                    <AdminProtectedRoute requiredPermission="canAccessAccounts">
                      <AdminAccountDetail />
                    </AdminProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/client-assets" 
                  element={
                    <AdminProtectedRoute>
                      <AdminClientAssets />
                    </AdminProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/vault" 
                  element={
                    <AdminProtectedRoute requiredPermission="canAccessVault">
                      <AdminVault />
                    </AdminProtectedRoute>
                  } 
                />
                <Route 
                  path="/admin/requests/:id" 
                  element={
                    <AdminProtectedRoute>
                      <AdminRequestDetail />
                    </AdminProtectedRoute>
                  }
                />
                <Route path="/admin/finance" element={<AdminProtectedRoute requiredPermission="canAccessFinance"><AdminFinance /></AdminProtectedRoute>} />
                <Route path="/admin/finance/expenses" element={<AdminProtectedRoute requiredPermission="canAccessFinance"><AdminExpenses /></AdminProtectedRoute>} />
                <Route path="/admin/finance/expenses/new" element={<AdminProtectedRoute requiredPermission="canAccessFinance"><AdminExpenseForm /></AdminProtectedRoute>} />
                <Route path="/admin/finance/expenses/:id" element={<AdminProtectedRoute requiredPermission="canAccessFinance"><AdminExpenseForm /></AdminProtectedRoute>} />
                <Route path="/admin/finance/expenses/by-client" element={<AdminProtectedRoute requiredPermission="canAccessFinance"><AdminExpensesByClient /></AdminProtectedRoute>} />
                <Route path="/admin/finance/expenses/client/:clientId" element={<AdminProtectedRoute requiredPermission="canAccessFinance"><AdminClientExpenses /></AdminProtectedRoute>} />
                <Route path="/admin/finance/income/manual" element={<AdminProtectedRoute requiredPermission="canAccessFinance"><AdminManualIncome /></AdminProtectedRoute>} />
                <Route path="/admin/finance/income/manual/new" element={<AdminProtectedRoute requiredPermission="canAccessFinance"><AdminManualIncomeForm /></AdminProtectedRoute>} />
                <Route path="/admin/finance/income/manual/:id" element={<AdminProtectedRoute requiredPermission="canAccessFinance"><AdminManualIncomeForm /></AdminProtectedRoute>} />
                <Route path="/admin/finance/income/stripe" element={<AdminProtectedRoute requiredPermission="canAccessFinance"><AdminStripeIncome /></AdminProtectedRoute>} />
                <Route path="/admin/finance/revenue" element={<AdminProtectedRoute requiredPermission="canAccessFinance"><AdminAllRevenue /></AdminProtectedRoute>} />
                <Route path="/admin/finance/profit" element={<AdminProtectedRoute requiredPermission="canAccessFinance"><AdminProfitReport /></AdminProtectedRoute>} />
                <Route path="/admin/finance/audit" element={<AdminProtectedRoute requiredPermission="canAccessFinance"><AdminAuditLog /></AdminProtectedRoute>} />
                <Route path="/admin/finance/scenarios" element={<AdminProtectedRoute requiredPermission="canAccessFinance"><AdminForecastScenarios /></AdminProtectedRoute>} />
                <Route path="/admin/finance/forecast" element={<AdminProtectedRoute requiredPermission="canAccessFinance"><AdminForecastDetail /></AdminProtectedRoute>} />
                <Route path="/admin/team" element={<AdminProtectedRoute requiredPermission="canAccessTeam"><AdminTeam /></AdminProtectedRoute>} />
                <Route path="/admin/logo-orders" element={<AdminProtectedRoute><AdminLogoOrders /></AdminProtectedRoute>} />
                <Route path="/admin/leads" element={<AdminProtectedRoute><AdminLeads /></AdminProtectedRoute>} />
                <Route path="/admin/partners" element={<AdminProtectedRoute><AdminTrustedPartners /></AdminProtectedRoute>} />
                <Route path="/admin/leads/:id" element={<AdminProtectedRoute><AdminLeadDetail /></AdminProtectedRoute>} />
                <Route path="/admin/website-reviews" element={<AdminProtectedRoute><AdminWebsiteReviews /></AdminProtectedRoute>} />
                <Route path="/admin/homepage-previews" element={<AdminProtectedRoute><AdminHomepagePreviews /></AdminProtectedRoute>} />
                
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </AuthProvider>
      </ErrorBoundary>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
